import { useCallback } from "react";
import { useGtmContext } from "../../contexts";
import { AnalyticEvent, AnalyticEventsDataTypes, AnalyticsEventMap, CustomEventName } from "@sweatpals/shared";
import { gtmAnalyticsEventMapper, gtmAnalyticsTrackingDataMapper } from "../../utils";

/**
 * Hook for interacting with Google Tag Manager.
 *
 * Provides functionality to log typed analytics events.
 *
 * @example
 * ```tsx
 * const { logEventTyped } = useGTMEventTracking();
 *
 * logEventTyped('login_success', { method: 'email' });
 *
 * ```
 *
 * @returns An object with methods to interact with Google Tag Manager
 * @note This hook can only be used within the scope of a GtmContext provider
 */
export const useGTMEventTracking = <TEvents extends AnalyticsEventMap = AnalyticEventsDataTypes>()  => {
  const { baseEventTrackingData, setUserDataExternaly, dataLayerPush } = useGtmContext()

  const logEventTyped = useCallback(
		<T extends CustomEventName<TEvents>>(
			event: T,
			metadata: AnalyticEvent<T, TEvents>
		) => {
			try {

				const mappedData = gtmAnalyticsTrackingDataMapper(event as CustomEventName<AnalyticEventsDataTypes>, metadata);
				const mappedEvent = gtmAnalyticsEventMapper(event as CustomEventName<AnalyticEventsDataTypes>);

				const payload = {
					event: mappedEvent,
          ...baseEventTrackingData,
					...mappedData,
				}

        dataLayerPush?.(payload);

				console.info('[GTM] Event logged: ', { event: mappedEvent, metadata: payload });
			} catch (e) {
				console.error('[GTM] Error while logging event', e);
			}
		},
		[baseEventTrackingData, dataLayerPush]
	);

  return {
    logEventTyped,
    setUserDataExternaly,
    baseEventTrackingData,
  }
}
